import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, FileText, Shield, Users, Clock, AlertTriangle } from 'lucide-react';

const TermsOfService: React.FC = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-startup-navy via-startup-teal to-startup-peach text-white animate-fade-in">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-light mb-8 leading-tight">
              Terms of
              <br />
              <span className="font-bold">Service</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-12 leading-relaxed">
              The rules and guidelines for using our platform
            </p>
            <div className="flex items-center justify-center gap-4 text-black-400">
              <FileText className="w-5 h-5" />
              <span>Last updated: August 15, 2025</span>
            </div>
          </div>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-32 bg-white text-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <div className="mb-16">
              <h2 className="text-3xl font-medium text-black mb-6">1. Acceptance of Terms</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                By accessing and using the StartupStreak platform ("Service"), you accept and agree to be bound by 
                the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
              </p>
              <p className="text-gray-700 leading-relaxed">
                These Terms of Service ("Terms") govern your use of our website and services operated by StartupStreak 
                ("Company," "we," "us," or "our").
              </p>
            </div>

            <div className="mb-16">
              <h2 className="text-3xl font-medium text-black mb-6">2. Description of Service</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                StartupStreak is a business acceleration platform that provides:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
                <li>AI-powered business idea validation</li>
                <li>30-day streak planning and accountability tools</li>
                <li>Launch playbooks and templates</li>
                <li>ROI tracking and analytics</li>
                <li>Community features and peer support</li>
                <li>Educational content and resources</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                The Service is designed to help entrepreneurs validate ideas, build businesses, and achieve 
                their first paying customer within 30 days.
              </p>
            </div>

            <div className="mb-16">
              <h2 className="text-3xl font-medium text-black mb-6">3. User Accounts and Registration</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                To access certain features of the Service, you must create an account. You agree to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
                <li>Provide accurate, current, and complete information during registration</li>
                <li>Maintain and promptly update your account information</li>
                <li>Maintain the security of your password and account</li>
                <li>Accept responsibility for all activities under your account</li>
                <li>Notify us immediately of any unauthorized use of your account</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                You must be at least 18 years old to create an account and use our services.
              </p>
            </div>

            <div className="mb-16">
              <h2 className="text-3xl font-medium text-black mb-6">4. Subscription and Payment Terms</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We offer subscription-based access to our premium features:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
                <li><strong>Monthly Plan:</strong> ₹1000/month with automatic renewal</li>
                <li><strong>Yearly Plan:</strong> ₹10,000/year with automatic renewal</li>
                <li><strong>Free Trial:</strong> 7-day free trial for new users</li>
                <li><strong>Cancellation:</strong> Cancel anytime through your account settings</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-4">
                Payment terms:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
                <li>All fees are non-refundable except as required by law</li>
                <li>Prices may change with 30 days notice</li>
                <li>Failed payments may result in service suspension</li>
                <li>Refunds are provided at our discretion</li>
              </ul>
            </div>

            <div className="mb-16">
              <h2 className="text-3xl font-medium text-black mb-6">5. Acceptable Use Policy</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                You agree not to use the Service to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe on intellectual property rights</li>
                <li>Harass, abuse, or harm other users</li>
                <li>Spread malware or attempt to compromise security</li>
                <li>Use automated systems to access the service</li>
                <li>Share account credentials with others</li>
                <li>Attempt to reverse engineer our platform</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to terminate accounts that violate these terms.
              </p>
            </div>

            <div className="mb-16">
              <h2 className="text-3xl font-medium text-black mb-6">6. AI-Powered Features</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our platform uses artificial intelligence to provide personalized business insights:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
                <li><strong>Idea Validation:</strong> AI analysis of business ideas and market potential</li>
                <li><strong>Task Generation:</strong> Personalized daily tasks based on your progress</li>
                <li><strong>Content Recommendations:</strong> AI-curated resources and playbooks</li>
                <li><strong>Performance Analytics:</strong> Data-driven insights and recommendations</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-4">
                Important limitations:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
                <li>AI insights are for informational purposes only</li>
                <li>We do not guarantee business success or profitability</li>
                <li>Users should exercise their own judgment and due diligence</li>
                <li>AI recommendations may not be suitable for all situations</li>
              </ul>
            </div>

            <div className="mb-16">
              <h2 className="text-3xl font-medium text-black mb-6">7. Intellectual Property Rights</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                The Service and its original content, features, and functionality are owned by StartupStreak 
                and are protected by international copyright, trademark, patent, trade secret, and other 
                intellectual property laws.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                You retain ownership of your business ideas and content, but grant us a license to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
                <li>Process your data to provide our services</li>
                <li>Use anonymized data for platform improvement</li>
                <li>Display your content within the platform</li>
                <li>Provide customer support and troubleshooting</li>
              </ul>
            </div>

            <div className="mb-16">
              <h2 className="text-3xl font-medium text-black mb-6">8. Privacy and Data Protection</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Your privacy is important to us. Our collection and use of personal information is governed 
                by our Privacy Policy, which is incorporated into these Terms by reference.
              </p>
              <p className="text-gray-700 leading-relaxed">
                By using our Service, you consent to the collection and use of information as outlined in 
                our Privacy Policy, including the use of AI and machine learning to improve your experience.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                For full details on our data practices, please review our
                <Link to="/privacy" className="text-blue-600 underline ml-1">Privacy Policy</Link> and how we use cookies in our
                <Link to="/cookie-policy" className="text-blue-600 underline ml-1">Cookie Policy</Link>.
              </p>
            </div>

            <div className="mb-16">
              <h2 className="text-3xl font-medium text-black mb-6">9. Disclaimers and Limitations</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                The Service is provided "as is" and "as available" without warranties of any kind. 
                We disclaim all warranties, express or implied, including:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
                <li>Warranties of merchantability or fitness for a particular purpose</li>
                <li>Warranties that the service will be uninterrupted or error-free</li>
                <li>Warranties regarding the accuracy of AI insights or recommendations</li>
                <li>Warranties of business success or profitability</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                In no event shall we be liable for indirect, incidental, special, consequential, 
                or punitive damages arising from your use of the Service.
              </p>
            </div>

            <div className="mb-16">
              <h2 className="text-3xl font-medium text-black mb-6">10. Business Success Disclaimer</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>Important:</strong> StartupStreak is a business acceleration platform that provides 
                tools, guidance, and accountability. However:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
                <li>We do not guarantee business success, profitability, or specific outcomes</li>
                <li>Business results depend on many factors beyond our control</li>
                <li>AI insights and recommendations are for informational purposes only</li>
                <li>Users must exercise their own judgment and due diligence</li>
                <li>Past performance does not guarantee future results</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                By using our Service, you acknowledge that business success requires effort, 
                market conditions, and many other factors that we cannot control or guarantee.
              </p>
            </div>

            <div className="mb-16">
              <h2 className="text-3xl font-medium text-black mb-6">11. Termination</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may terminate or suspend your account and access to the Service immediately, 
                without prior notice, for any reason, including:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
                <li>Breach of these Terms</li>
                <li>Fraudulent or illegal activity</li>
                <li>Non-payment of fees</li>
                <li>Violation of acceptable use policies</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                Upon termination, your right to use the Service will cease immediately. 
                We may delete your account and data at our discretion.
              </p>
            </div>

            <div className="mb-16">
              <h2 className="text-3xl font-medium text-black mb-6">12. Changes to Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to modify these Terms at any time. We will notify users of any 
                material changes by posting the new Terms on this page and updating the "Last updated" date. 
                Your continued use of the Service after any changes constitutes acceptance of the new Terms.
              </p>
            </div>

            <div className="mb-16">
              <h2 className="text-3xl font-medium text-black mb-6">13. Governing Law</h2>
              <p className="text-gray-700 leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of India, 
                without regard to its conflict of law provisions. Any disputes arising from these Terms 
                or your use of the Service shall be resolved through binding arbitration.
              </p>
            </div>

            <div className="mb-16">
              <h2 className="text-3xl font-medium text-black mb-6">14. Contact Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700 mb-2">
                  <strong>Email:</strong> legal@startupstreak.com
                </p>
                <p className="text-gray-700 mb-2">
                  <strong>Address:</strong> StartupStreak, Legal Team
                </p>
                <p className="text-gray-700">
                  <strong>Response Time:</strong> We aim to respond to all legal inquiries within 72 hours.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 bg-gradient-to-br from-startup-navy via-startup-teal to-startup-peach text-white animate-fade-in">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-6xl font-light mb-8 leading-tight">
              Questions About Our Terms?
            </h2>
            <p className="text-xl text-gray-300 mb-12 leading-relaxed">
              We're committed to transparency. Reach out if you need clarification 
              on any terms or conditions.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-startup-teal text-black hover:bg-startup-gold hover:text-startup-navy text-lg px-12 py-4 font-medium transition-all hover:ring-2 hover:ring-startup-gold/60">
                  Contact Us
                </Button>
              </Link>
              <Link to="/signup">
                <Button size="lg" variant="outline" className="border-startup-teal text-black hover:bg-white hover:text-startup-navy hover:border-white text-lg px-12 py-4 font-medium transition-all">
                  Start Your Streak
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;
